import React, { useCallback, useEffect } from "react";

// UI libraries
import {
  Accordion,
  Button,
  Col,
  Form,
  Modal,
  ModalBody,
  ModalFooter,
  ModalTitle,
  Row,
} from "react-bootstrap";
import ModalHeader from "react-bootstrap/esm/ModalHeader";
import DatePicker from "react-datepicker";
import { Typeahead } from "react-bootstrap-typeahead";

// shared components
import NavigationBar from "../navigation/NavigationBar";
import SidebarClinician from "../sidebars/SidebarClinician";
import Footer from "../Footer";
import ErrorBoundary from "../../ErrorBoundary";
import SessionEndReason from "../sharedComponents/SessionEndReason";
import SessionCounts from "../sharedComponents/SessionCounts";
import { Labels, PatientLabels } from "../sharedComponents/PatientLabels";
import SurveyStatusModal from "../sharedComponents/SurveyStatusModal";
import ResultStateModal from "../sharedComponents/ResultStateModal";
import LoadingModal from "../sharedComponents/LoadingModal";
import PatientTable from "../sharedComponents/TableComponents/PatientTable";
import { GenderList } from "../componentsClinicAdmin/HomeClinicAdmin";
import { fetchAndShowDiscardInfo } from "../../helper/surveyUtils";

// API services
import {
  clinicianAssignClinician,
  editPatient,
} from "../../services/postFunctions";
import { Diagnosis, Patient } from "../../services/getFunctions";

// custom hooks
import { usePartialState } from "../../helper/Hooks/usePartialState";
import { useAuth } from "../../contexts/AuthContext";
import { useAccordion } from "../../contexts/AccordionContext";
import { usePatientTableState } from "../../helper/Hooks/usePatientTableState";
import { useAlertHelpers } from "../../helper/Hooks/useAlertHelpers";

// others
import {
  formatLocalDate,
  getGermanTodayDateObject,
  handleRawDateInput,
  parseLocalDate,
} from "../../helper/dateHelpers";
import { toErrorType } from "../../helper/alertBoxes";

import "react-bootstrap-typeahead/css/Typeahead.css";

/**
 * @patients: the raw data fetched from backend.
 * @patientsDisplay: adapted from patients, added with JSX.Element to display patients in table.
 */
interface HomeClinicianState {
  // Configuration & Auth
  user_type: string;
  clinic_id: string;
  clinic_name: string;
  isAuthenticated: boolean;

  // Modal states
  modalAssignClinicianOpen: boolean;
  modalDiagnosisOpen: boolean;
  modalEditPatientOpen: boolean;
  modalResultOpen: boolean;
  modalSurveyStateOpen: boolean;

  // Date states
  admission: Date;
  discharge: Date;
  editAdmission: Date;
  editDischarge: Date;
  editBirthday: Date;
  dateChangeMessage: string;
  admissionChanged: boolean;
  dischargeChanged: boolean;

  // Form states
  code: string;
  gender: string;
  diagnosesPatient: Diagnosis[];
  username: string;
  patient_id: string;
  clinician: string;
  clinician_id: string;
  clinician_username: string;
  clinician_from_username: string;
  clinician_from_id: string;
  clinician_to: string;

  // Data lists
  genderList: GenderList[];
  labels: Labels;

  // Other states / Entities
  currentPatient: Patient;
}

interface HomeClinicianProps {
  title: string;
}

const HomeClinician = ({ title }: HomeClinicianProps) => {
  const { state: authState } = useAuth();
  const { state: appState, handleAccordion } = useAccordion();

  const { showAlertModal, reportEditPatientError } = useAlertHelpers();

  const {
    patients,
    pagination,
    diagnoses,
    clinicians,
    isLoading,
    refreshCurrentPage,
    loadPage,
    handlePageChange,
    handlePageSizeChange,
    updateDateFilter,
    updateFieldFilter,
    updateLabelFilter,
    updateReportStateFilter,
    updateSort,
  } = usePatientTableState();

  // initial state
  const initialState: HomeClinicianState = {
    user_type: "clinician",
    currentPatient: {
      viewed: "",
      duplicate: false,
      date_of_birth: "",
      admission: "",
      discharge: "",
      code: "",
      clinician_username: "",
      clinician_id: "",
      diagnoses: [],
      gender: "", //maybe reduce to the possible values
      id: "",
      survey_statuses: [],
      survey_time_windows: [],
      labels: [],
      last_update: 0,
    },
    clinic_id: "",
    clinic_name: "",
    username: "",
    patient_id: "",
    code: "",
    clinician_from_id: "",
    isAuthenticated: authState.isAuthenticated,
    modalSurveyStateOpen: false,
    modalResultOpen: false,
    modalAssignClinicianOpen: false,

    clinician_to: "",
    clinician: "",
    diagnosesPatient: [],
    modalDiagnosisOpen: false,
    modalEditPatientOpen: false,
    gender: "",
    editBirthday: getGermanTodayDateObject(),
    editAdmission: getGermanTodayDateObject(),
    editDischarge: getGermanTodayDateObject(),
    admissionChanged: false,
    dischargeChanged: false,
    clinician_id: "",
    clinician_username: "",
    clinician_from_username: "",
    admission: getGermanTodayDateObject(),
    discharge: getGermanTodayDateObject(),
    genderList: [
      { value: "FEMALE", name: "Weiblich" },
      { value: "MALE", name: "Männlich" },
      { value: "DIVERSE", name: "Divers" },
    ],
    labels: [],
    dateChangeMessage: "",
  };

  const [state, updateState] = usePartialState(initialState);

  //load initial data on mount
  useEffect(() => {
    document.title = title;

    // Initialize component state
    const initState: Partial<HomeClinicianState> = {
      clinic_id: authState.userInfo?.clinicId ?? "",
      clinic_name: authState.userInfo?.clinicName ?? "",
      username: authState.userInfo?.userName ?? "",
    };
    updateState(initState);

    // Load patients if authenticated
    if (authState.isAuthenticated && authState.userInfo?.clinicId) {
      loadPage(1, 100);
    }
  }, [
    title,
    authState.isAuthenticated,
    authState.userInfo?.clinicId,
    authState.userInfo?.clinicName,
    authState.userInfo?.userName,
    updateState,
    loadPage,
  ]);

  // modal handlers
  const closeEditPatientModal = useCallback(
    () =>
      updateState({
        modalEditPatientOpen: false,
        code: "",
        gender: "",
      }),
    [updateState],
  );

  const closeAssignClinicianModal = () =>
    updateState({
      modalAssignClinicianOpen: false,
      code: "",
      clinician: "",
      clinician_from_username: "",
    });

  //input handlers
  const handleEditBirthdate = (date) => {
    updateState({ editBirthday: date });
  };

  /**Method for handle patients diagnoses.
   * Each diagnoses that is selected is written to the diagnosesPatient list.
   * If no diagnosis is selected, an alert is displayed.
   */
  const handleEditDiagnosesList = useCallback(
    (event: React.SubmitEvent<HTMLFormElement>) => {
      event.preventDefault();
      const checkboxes = Array.from(
        event.currentTarget.querySelectorAll<HTMLInputElement>(
          'input[type="checkbox"]:checked',
        ),
      );

      const diagnoses: Diagnosis[] = checkboxes.map((checkbox) => ({
        description: checkbox.dataset.name || "",
        id: checkbox.value,
      }));

      if (diagnoses.length === 0) {
        showAlertModal("Sie müssen mindestens eine Diagnose wählen.");
      } else {
        updateState({
          diagnosesPatient: diagnoses,
          modalDiagnosisOpen: false,
        });
      }
    },
    [showAlertModal, updateState],
  );

  /**Set modalEditPatientOpen on true, if the modal is opened.
   * Set the actual states of the selected patient.
   */
  const openEditPatientModal = useCallback(
    (event) => {
      event.preventDefault();
      const code = event.currentTarget.id;
      const patient = patients.find((p) => p.code === code);

      if (!patient) {
        console.error("Could not get patient");
        return;
      }

      let newBirthday: Date;
      let newAdmission: Date;
      let newDischarge: Date;
      try {
        newBirthday = parseLocalDate(patient.date_of_birth);
        newAdmission = parseLocalDate(patient.admission);
        newDischarge = parseLocalDate(patient.discharge);
      } catch (error) {
        console.error("Failed to parse patient dates:", error);
        return;
      }

      let gender = patient.gender;
      if (patient.gender === "Weiblich") {
        gender = "FEMALE";
      } else if (patient.gender === "Männlich") {
        gender = "MALE";
      } else if (patient.gender === "Divers") {
        gender = "DIVERSE";
      }

      const patientDiagnoses = patient.diagnoses || [];

      updateState({
        modalEditPatientOpen: true,
        editBirthday: newBirthday,
        editAdmission: newAdmission,
        editDischarge: newDischarge,
        admissionChanged: false,
        dischargeChanged: false,
        clinician_id: patient.clinician_id,
        clinician_username: patient.clinician_username,
        diagnosesPatient: patientDiagnoses,
        patient_id: patient.id,
        gender: gender,
        code: patient.code,
        labels: patient.labels,
        dateChangeMessage: "",
        currentPatient: patient,
      });

      console.info("Diagnosis(n): ", patient.diagnoses);
    },
    [patients, updateState],
  );

  /**Method to update the gender state, when it is changed.
   * @param text {ChangeEvent} onChange delivers the event with the selected gender
   */
  const handleGender = (text: React.ChangeEvent<HTMLSelectElement>) => {
    const value = text.target.value === "Auswählen..." ? "" : text.target.value;
    updateState({ gender: value });
    console.log("Gender: ", text.target.value);
  };

  /**Method to update the diagnosis state, when it is changed.
   * @param text {ChangeEvent} onChange delivers the event with the slected diagnosis
   */
  const handleDiagnosis = (items: Diagnosis[]) => {
    try {
      if (items && Array.isArray(items) && items.length) {
        updateState({
          diagnosesPatient: items.filter(
            (item, i, arr) => arr.findIndex((t) => t.id === item.id) === i,
          ),
        });
      } else {
        updateState({ diagnosesPatient: [] });
      }
    } catch (error) {
      throw new reportEditPatientError(toErrorType(error));
    }
  };

  /**Method to update the edited admission date state, when it is changed.
   * @param date {date} date from datepicker or date input.
   */
  const handleEditAdmissionDate = useCallback(
    (date: Date | null) => {
      if (date) {
        updateState({
          dateChangeMessage:
            "Achtung: Durch das Ändern von Aufnahme- oder Entlassdatum können bisherige Befragungen ungültig werden. Alle Datumsangaben in deutscher Ortszeit (MEZ/MESZ).",
          editAdmission: date,
          admissionChanged: true,
        });
      }
    },
    [updateState],
  );

  const handleEditAdmissionCommit = useCallback(async () => {
    if (!state.admissionChanged) {
      return;
    }
    const date = state.editAdmission;
    if (!date) return;

    if (date >= state.editDischarge) {
      updateState({ admissionChanged: false });
      showAlertModal("Das Entlassdatum muss nach dem Aufnahmedatum liegen.");
      return;
    }
    updateState({ admissionChanged: false });

    await fetchAndShowDiscardInfo(
      state.patient_id,
      date,
      state.editDischarge,
      showAlertModal,
    );
  }, [
    state.admissionChanged,
    state.editAdmission,
    state.editDischarge,
    updateState,
    showAlertModal,
    state.patient_id,
  ]);

  /**Method to update the edited discharge date state, when it is changed.
   * @param date {date} date from datepicker or date input.
   */
  const handleEditDischargeDate = useCallback(
    async (date: Date | null) => {
      if (date) {
        updateState({
          dateChangeMessage:
            "Achtung: Durch das Ändern von Aufnahme- oder Entlassdatum können bisherige Befragungen ungültig werden. Alle Datumsangaben in deutscher Ortszeit (MEZ/MESZ).",
          editDischarge: date,
          dischargeChanged: true,
        });
      }
    },
    [updateState],
  );

  const handleEditDischargeCommit = useCallback(async () => {
    if (!state.dischargeChanged) {
      return;
    }
    const date = state.editDischarge;
    if (!date) return;

    if (date <= state.editAdmission) {
      updateState({ dischargeChanged: false });
      showAlertModal("Das Entlassdatum muss nach dem Aufnahmedatum liegen.");
      return;
    }
    updateState({ dischargeChanged: false });

    await fetchAndShowDiscardInfo(
      state.patient_id,
      state.editAdmission,
      date,
      showAlertModal,
    );
  }, [
    state.dischargeChanged,
    state.editDischarge,
    state.editAdmission,
    state.patient_id,
    showAlertModal,
    updateState,
  ]);

  /**Method to handle patient edit.*/
  const handleEditPatient = useCallback(
    async (event: React.SubmitEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (state.admissionChanged) {
        await handleEditAdmissionCommit();
        return;
      }
      if (state.dischargeChanged) {
        await handleEditDischargeCommit();
        return;
      }
      updateState({ dateChangeMessage: "" });

      if (!state.editBirthday || !state.editAdmission || !state.editDischarge) {
        showAlertModal("Bitte füllen Sie alle Datumsfelder aus.");
        return;
      }

      if (state.editDischarge <= state.editAdmission) {
        showAlertModal("Das Entlassdatum muss nach dem Aufnahmedatum liegen.");
        return;
      }

      const patientData = {
        date_of_birth: formatLocalDate(state.editBirthday),
        admission: formatLocalDate(state.editAdmission),
        discharge: formatLocalDate(state.editDischarge),
        clinician_id: state.clinician_id,
        clinician_username: state.clinician_username,
        diagnoses: state.diagnosesPatient,
        id: state.patient_id,
        gender: state.gender,
        code: state.code,
        labels: state.labels,
        last_update: state.currentPatient.last_update,
      };

      try {
        await editPatient(patientData);
        closeEditPatientModal();
        await refreshCurrentPage();
      } catch (error) {
        reportEditPatientError(toErrorType(error));
        console.error("Catch", error);
      }
    },
    [
      state,
      updateState,
      refreshCurrentPage,
      closeEditPatientModal,
      reportEditPatientError,
      showAlertModal,
      handleEditAdmissionCommit,
      handleEditDischargeCommit,
    ],
  );

  /**Set modalAssignClinicianOpen on true, if the modal is opened.
   * Set the current patient states.
   */
  const openAssignClinicianModal = (event) => {
    event.preventDefault();
    const code = event.currentTarget.id;
    const patient = patients.find((p) => p.code === code);

    if (!patient) {
      console.error("Patient doesn't exist.");
      return;
    }

    updateState({
      modalAssignClinicianOpen: true,
      patient_id: patient.id,
      code: patient.code,
      clinician_from_id: patient.clinician_id,
      clinician_from_username: patient.clinician_username,
      clinician: "", //reset the dropdown state
    });
  };

  /**
   * open the modal and load the data, change the current patient to clicked patient
   * and load the reports for the current patient.
   */
  const openSurveyStateModal = (event) => {
    event.preventDefault();
    const id = event.target.id;
    console.log("event id: " + id);
    console.log("Patients for finding: " + patients);
    const patient = patients.find((p) => p.id === id);

    if (patient) {
      if (state.currentPatient.id !== id) {
        updateState({
          currentPatient: patient,
        });
      }
    } else {
      console.error(`Patient with id ${id} not found.`);
    }

    updateState({
      modalSurveyStateOpen: true,
      modalResultOpen: false,
    });
  };
  // UI Pop-up for viewing survey results
  const openResultModal = (event) => {
    event.preventDefault();
    const id = event.target.id;
    const patient = patients.find((p) => p.id === id);

    updateState({
      modalResultOpen: true,
      modalSurveyStateOpen: false,
    });

    if (patient) {
      if (state.currentPatient.id !== id) {
        updateState({
          currentPatient: patient,
        });
      }
    } else {
      console.error(`Patient with id ${id} not found.`);
    }
  };

  /**Method for handle clinician assign.
   * The input data are stringified to JSON.
   * Then send JSON to backend api.
   */
  const handleClinicianAssign = async (event) => {
    event.preventDefault();

    const assignmentData = {
      patient_id: state.patient_id,
      clinician_from: state.clinician_from_id,
      clinician_to: state.clinician,
    };

    try {
      await clinicianAssignClinician(assignmentData);
      showAlertModal(
        "Der*die Patient*in wurde erfolgreich einem neuen Mitarbeitenden zugewiesen.",
      );
      closeAssignClinicianModal();
      await refreshCurrentPage();
    } catch (error) {
      showAlertModal(
        "Irgendetwas ist schief gelaufen. Der*die Patient*in wurde keinem neuen Mitarbeitenden zugewiesen.",
      );
      console.error("Assign Clinician Fail: ", error);
    }
  };

  return (
    <ErrorBoundary>
      <div id="body">
        {/* navigation bar (bar on the top) */}
        <NavigationBar />
        <div className="container-fluid">
          <div className="row">
            {/* Sidebar (bar on the side) */}
            <SidebarClinician />
            {/* Content */}
            <div className="col container p-3 " id="main-content">
              <Accordion
                className="pb-2"
                activeKey={appState.clinician.myPatients}
              >
                <Accordion.Item eventKey="0" className="card-default">
                  <Accordion.Header
                    onClick={() =>
                      handleAccordion({
                        input: "myPatients",
                        userType: "clinician",
                      })
                    }
                  >
                    Meine Patient*innen
                  </Accordion.Header>
                  <Accordion.Body>
                    {/**the patient table */}
                    <PatientTable
                      patients={patients}
                      userType={state.user_type}
                      pagination={pagination}
                      openEditPatientModal={openEditPatientModal}
                      openAssignClinicianModal={openAssignClinicianModal}
                      openResultModal={openResultModal}
                      openSurveyStateModal={openSurveyStateModal}
                      onPageChange={handlePageChange}
                      onPageSizeChange={handlePageSizeChange}
                      updateStringFilter={updateFieldFilter}
                      updateSelectFilter={updateFieldFilter}
                      updateDateFilter={updateDateFilter}
                      updateLabelFilter={updateLabelFilter}
                      updateReportStateFilter={updateReportStateFilter}
                      updateSort={updateSort}
                    />
                  </Accordion.Body>
                </Accordion.Item>
              </Accordion>
              <Accordion
                className="pb-2"
                defaultActiveKey="0"
                activeKey={appState.clinician.clinics}
              >
                <Accordion.Item eventKey="1" className="card-default">
                  <Accordion.Header
                    onClick={() =>
                      handleAccordion({
                        input: "clinics",
                        userType: "clinician",
                      })
                    }
                  >
                    Klinik
                  </Accordion.Header>
                  <Accordion.Body>
                    Ihre Klinik ist die{" "}
                    <em>{authState.userInfo?.clinicName}</em>.
                  </Accordion.Body>
                </Accordion.Item>
              </Accordion>

              {state.modalSurveyStateOpen && (
                <div>
                  <SurveyStatusModal
                    currentPatient={state.currentPatient}
                    modalSurveyStateOpen={state.modalSurveyStateOpen}
                    closeSurveyStateModal={() => {
                      updateState({ modalSurveyStateOpen: false });
                    }}
                  />
                </div>
              )}

              {state.modalResultOpen && (
                <div>
                  <ResultStateModal
                    currentPatient={state.currentPatient}
                    modalResultOpen={state.modalResultOpen}
                    closeResultModal={() => {
                      updateState({ modalResultOpen: false });
                    }}
                    clinic_id={state.clinic_id}
                    clinic_name={state.clinic_name}
                    username={state.username}
                  />
                </div>
              )}

              <Modal
                backdrop="static"
                show={state.modalEditPatientOpen}
                onHide={closeEditPatientModal}
              >
                <ModalHeader closeButton>
                  <ModalTitle>
                    Patient*in <em>{state.code}</em> bearbeiten
                  </ModalTitle>
                </ModalHeader>
                <ModalBody>
                  <Form noValidate onSubmit={handleEditPatient}>
                    <Row className="mb-3">
                      <Col>
                        <Form.Group controlId="formGridBirthday">
                          <Form.Label>Geburtsdatum</Form.Label>
                          <DatePicker
                            className="form-control"
                            placeholderText="Datum auswählen"
                            dateFormat="dd.MM.yyyy"
                            selected={state.editBirthday || null}
                            onChange={(date) => {
                              handleEditBirthdate(date);
                            }}
                            onChangeRaw={handleRawDateInput}
                          />
                        </Form.Group>
                      </Col>
                      <Col>
                        <Form.Group controlId="gender-select">
                          <Form.Label>Geschlecht</Form.Label>
                          <Form.Select
                            className="form-control"
                            required
                            value={state.gender}
                            onChange={handleGender}
                          >
                            {!state.gender && (
                              <option value="" disabled>
                                Bitte auswählen
                              </option>
                            )}
                            {state.genderList &&
                              state.genderList.length > 0 &&
                              state.genderList.map((gender) => (
                                <option key={gender.value} value={gender.value}>
                                  {gender.name}
                                </option>
                              ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row>
                      <Col>
                        <Form.Group>
                          <Form.Label htmlFor="diagnose-input">
                            Diagnose(n)
                          </Form.Label>
                          <Typeahead
                            id="basic-typeahead-example"
                            inputProps={{
                              id: "diagnose-input",
                            }}
                            selected={state.diagnosesPatient || []}
                            multiple={true}
                            size="sm"
                            align={"left"}
                            labelKey="description"
                            options={diagnoses as Diagnosis[]}
                            placeholder="Auswählen..."
                            clearButton={false}
                            onChange={(event) => {
                              handleDiagnosis(event as Diagnosis[]);
                            }}
                            maxResults={1000}
                            renderMenuItemChildren={(option) => (
                              <span className="text-wrap">
                                <b>{(option as Diagnosis).id}</b> -{" "}
                                {(option as Diagnosis).description}
                              </span>
                            )}
                            filterBy={(option, searchText) => {
                              return (
                                (option as Diagnosis).id
                                  .toLowerCase()
                                  .includes(searchText.text.toLowerCase()) ||
                                (option as Diagnosis).description
                                  .toLowerCase()
                                  .includes(searchText.text.toLowerCase())
                              );
                            }}
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row className="mb-3">
                      <Col>
                        <Form.Group>
                          <Form.Label>Aufnahmedatum ändern</Form.Label>
                          <DatePicker
                            className="form-control"
                            placeholderText="Datum auswählen"
                            selected={state.editAdmission || null}
                            onChange={(date) => {
                              handleEditAdmissionDate(date);
                            }}
                            onCalendarClose={handleEditAdmissionCommit}
                            dateFormat="dd.MM.yyyy"
                            onChangeRaw={handleRawDateInput}
                            preventOpenOnFocus
                          />
                        </Form.Group>
                      </Col>
                      <Col>
                        <Form.Group>
                          <Form.Label>Entlassdatum ändern</Form.Label>
                          <DatePicker
                            className="form-control"
                            placeholderText="Datum auswählen"
                            selected={state.editDischarge || null}
                            minDate={state.editAdmission}
                            onChange={(date) => {
                              handleEditDischargeDate(date);
                            }}
                            onCalendarClose={handleEditDischargeCommit}
                            dateFormat="dd.MM.yyyy"
                            onChangeRaw={handleRawDateInput}
                            preventOpenOnFocus
                          />
                        </Form.Group>
                      </Col>
                      {state.dateChangeMessage && (
                        <Form.Text className="text-danger mt-2">
                          {state.dateChangeMessage}
                        </Form.Text>
                      )}
                    </Row>
                    <PatientLabels
                      labels={state.labels}
                      setLabels={(labels: Labels) => {
                        updateState({ labels });
                      }}
                    />
                    <hr />
                    <Row className="mb-3">
                      <h5>Angaben zu Therapieende:</h5>
                    </Row>
                    <SessionCounts
                      labels={state.labels}
                      setLabels={(labels: Labels) => {
                        updateState({ labels });
                      }}
                    />
                    <SessionEndReason
                      labels={state.labels}
                      setLabels={(labels: Labels) => {
                        updateState({ labels });
                      }}
                    />
                    <Button type="submit">Speichern</Button>
                  </Form>
                </ModalBody>
                <ModalFooter>
                  <Button variant="danger" onClick={closeEditPatientModal}>
                    Abbrechen
                  </Button>
                </ModalFooter>
              </Modal>

              <Modal
                backdrop="static"
                size="lg"
                show={state.modalDiagnosisOpen}
                onHide={() => {
                  updateState({ modalDiagnosisOpen: false });
                }}
              >
                <ModalHeader>Wählen Sie die Diagnose(n) aus:</ModalHeader>
                <ModalBody>
                  <Form noValidate onSubmit={handleEditDiagnosesList}>
                    {diagnoses &&
                      diagnoses.length > 0 &&
                      diagnoses.map((diagnosis) => {
                        return (state.diagnosesPatient || []).some(
                          (diagPatient) => {
                            //security check
                            return (
                              diagnosis.description === diagPatient.description
                            );
                          },
                        ) ? (
                          <Form.Check
                            key={diagnosis.id}
                            type="checkbox"
                            id={diagnosis.id}
                            data-name={diagnosis.description}
                            label={diagnosis.description}
                            value={diagnosis.id}
                            defaultChecked
                          />
                        ) : (
                          <Form.Check
                            key={diagnosis.id}
                            type="checkbox"
                            id={diagnosis.id}
                            data-name={diagnosis.description}
                            label={diagnosis.description}
                            value={diagnosis.id}
                          />
                        );
                      })}
                    <Button type="submit">Speichern</Button>
                  </Form>
                </ModalBody>
                <ModalFooter>
                  <Button
                    variant="danger"
                    onClick={() => {
                      updateState({ modalDiagnosisOpen: false });
                    }}
                  >
                    Abbrechen
                  </Button>
                </ModalFooter>
              </Modal>

              <Modal
                backdrop="static"
                show={state.modalAssignClinicianOpen}
                onHide={closeAssignClinicianModal}
              >
                <ModalHeader closeButton>
                  <ModalTitle>Anderen Mitarbeitenden zuweisen</ModalTitle>
                </ModalHeader>
                <ModalBody>
                  <Form noValidate onSubmit={handleClinicianAssign}>
                    <Form.Group as={Row} className="mb-3">
                      <Form.Label column sm="5" htmlFor="patient-code">
                        Patient*in
                      </Form.Label>
                      <Col sm="7">
                        <Form.Control
                          id="patient-code"
                          className="form-control-plain"
                          plaintext
                          readOnly
                          defaultValue={state.code}
                        />
                      </Col>
                    </Form.Group>
                    <Form.Group as={Row} className="mb-3">
                      <Form.Label column sm="5">
                        Mitarbeitende ändern:
                      </Form.Label>
                      <Col sm="7">
                        <Form.Select
                          className="form-control"
                          required
                          value={state.clinician}
                          onChange={(e) => {
                            const val =
                              e.target.value === "Auswählen..."
                                ? ""
                                : e.target.value;
                            updateState({ clinician: val });
                          }}
                        >
                          <option>{state.clinician_from_username}</option>
                          {clinicians &&
                            clinicians.length > 0 &&
                            clinicians.map((c) => {
                              return c.username !==
                                state.clinician_from_username ? (
                                <option
                                  key={c.id}
                                  value={c.username}
                                  disabled={!c.enabled}
                                >
                                  {c.username}
                                </option>
                              ) : null;
                            })}
                        </Form.Select>
                      </Col>
                    </Form.Group>
                    <Button type="submit">Mitarbeitende ändern</Button>
                  </Form>
                </ModalBody>
                <ModalFooter>
                  <Button variant="danger" onClick={closeAssignClinicianModal}>
                    Abbrechen
                  </Button>
                </ModalFooter>
              </Modal>

              <div>
                <LoadingModal
                  modalLoadingOpen={isLoading}
                  closeLoadingModal={() => {}}
                />
              </div>
            </div>
            <Footer />
          </div>
        </div>
      </div>
    </ErrorBoundary>
  );
};

export default HomeClinician;
